import {
  Box,
  Button,
  Dropdown,
  Grid,
  GridColumn,
  Icon,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableHeaderSelectionCell,
  TableRow,
  TableSelectionCell,
  Text,
  Tooltip
} from "@modernatx/ui-kit-react";
import React, { FC, useState } from "react";
import { useHistory } from "react-router-dom";

import { SchedulePayModal } from "@/components/Payments/AutoPayModal/SchedulePayModal";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import {
  InvoiceListProps,
  PageSize,
  payableStatuses,
  SortableColumnKeys,
  SortOrder
} from "@/types/Invoice";
import {
  formatAddress,
  formatCurrency,
  formatInvoiceStatus,
  formatTimestampDate,
  getPaymentStatus
} from "@/utils/InvoicePay";

import { PaymentStatus } from "../../types/Invoice";

const InvoiceList: FC<InvoiceListProps> = ({
  invoiceList,
  pageSize,
  totalPage,
  totalInvoices,
  currentPage,
  sortDirection,
  sortProperty,
  onPageChange,
  onPageSizeChange,
  onSort
}) => {
  const history = useHistory();
  const isMultiInvoicePayAvailable = useFeatureFlag("MULTI_INVOICE_PAY");

  const [selectedInvoiceNos, setselectedInvoiceNos] = useState<string[]>([]);
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);
  const autoPayAndSchedulePayEnabled = useFeatureFlag(
    "IS_AUTO_PAY_SCHEDULE_PAY_ENABLED"
  );

  const allSelected = React.useMemo(() => {
    return (
      invoiceList.length > 0 && selectedInvoiceNos.length === invoiceList.length
    );
  }, [invoiceList, selectedInvoiceNos]);

  const someSelected = React.useMemo(() => {
    return (
      selectedInvoiceNos.length > 0 &&
      selectedInvoiceNos.length < invoiceList.length
    );
  }, [invoiceList, selectedInvoiceNos]);

  const handleSelectAll = () => {
    if (allSelected) {
      setselectedInvoiceNos([]);
    } else {
      setselectedInvoiceNos(invoiceList.map((invoice) => invoice.invoiceNo));
    }
  };

  const handleSelectRow = (invoiceNo: string) => {
    if (selectedInvoiceNos.includes(invoiceNo)) {
      setselectedInvoiceNos(
        selectedInvoiceNos.filter((id) => id !== invoiceNo)
      );
    } else {
      setselectedInvoiceNos([...selectedInvoiceNos, invoiceNo]);
    }
  };

  const handleSortClick = (property: SortableColumnKeys) => {
    const newSortDirection =
      sortProperty === property && sortDirection === SortOrder.ASC
        ? SortOrder.DESC
        : SortOrder.ASC;
    onSort({
      property,
      direction: newSortDirection
    });
  };

  const showActionBar = React.useMemo(() => {
    return someSelected || allSelected ? true : false;
  }, [allSelected, someSelected]);

  const invoiceTexts = React.useMemo(() => {
    const count = selectedInvoiceNos.length;
    return {
      someSelectedText: `${count} ${count === 1 ? "invoice" : "invoices"} selected`,
      payButtonText: count === 1 ? "Pay invoice" : "Pay invoices",
      scheduleButtonText:
        count === 1 ? "Schedule payment" : "Schedule payments",
      downloadButtonText:
        count === 1 ? "Download invoice" : "Download invoices",
      shareButtonText: count === 1 ? "Share invoice" : "Share invoices"
    };
  }, [selectedInvoiceNos.length]);

  const showPaymentButtons = React.useMemo(() => {
    const selectedInvoices = invoiceList.filter((invoice) =>
      selectedInvoiceNos.includes(invoice.invoiceNo)
    );
    return selectedInvoices.every((invoice) =>
      payableStatuses.includes(formatInvoiceStatus(invoice.status))
    );
  }, [invoiceList, selectedInvoiceNos]);

  const getActions = (status: PaymentStatus, invoiceNo: string) => {
    switch (status) {
      case PaymentStatus.Open:
      case PaymentStatus.Overdue:
      case PaymentStatus.DueSoon:
        return (
          <Button
            onClick={() => {
              history.push(`/pay?invoice=${invoiceNo}`);
            }}
            variant="outline"
          >
            Pay Now
          </Button>
        );
      default:
        return "--";
    }
  };

  const statusLegend = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "start",
        flexDirection: "column",
        gap: "spacing-5",
        minWidth: "fit-content",
        color: "text-default",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        whiteSpace: "normal",
        padding: "spacing-2"
      }}
    >
      <Text
        sx={{ minWidth: "fit-content", overflowWrap: "break-word" }}
        variant="body-2-regular"
      >
        <Text variant="body-2-bold">Pending: </Text>
        Credit card payments are typically reflected within 3-5 business days.
        Payments made by ACH or check are typically reflected within 7-10
        business days.
      </Text>
      <Text variant="body-2-regular">
        <Text variant="body-2-bold">Open: </Text>
        Unpaid.
      </Text>
      <Text variant="body-2-regular">
        <Text variant="body-2-bold">Due soon: </Text>5 days from due date.
      </Text>
      <Text variant="body-2-regular">
        <Text variant="body-2-bold">Overdue: </Text>Outstanding balance past
        due.
      </Text>
      <Text variant="body-2-regular">
        <Text variant="body-2-bold">Paid: </Text>Paid in full.
      </Text>
    </Box>
  );

  return (
    <Grid>
      {autoPayAndSchedulePayEnabled && showScheduleModal && (
        <SchedulePayModal
          isVisible={showScheduleModal}
          setVisible={setShowScheduleModal}
        />
      )}

      {showActionBar && (
        <GridColumn>
          {(someSelected || allSelected) && (
            <Text variant="body-1-regular">
              {invoiceTexts.someSelectedText}
            </Text>
          )}
          <Box
            sx={{
              display: "flex",
              gap: "spacing-3",
              mt: "spacing-3",
              mb: "spacing-6"
            }}
          >
            {/* TODO: hook up onClicks for buttons. https://modernatx.atlassian.net/browse/MD-332 */}
            {showPaymentButtons && (
              <Button variant="outline" onClick={() => {}}>
                {invoiceTexts.payButtonText}
              </Button>
            )}
            {showPaymentButtons && autoPayAndSchedulePayEnabled && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowScheduleModal(true);
                }}
              >
                {invoiceTexts.scheduleButtonText}
              </Button>
            )}
            <Button
              variant="outline"
              icon="download-simple"
              iconPosition="end"
              onClick={() => {}}
            >
              {invoiceTexts.downloadButtonText}
            </Button>
            <Button
              variant="outline"
              icon="share"
              iconPosition="end"
              onClick={() => {}}
            >
              {invoiceTexts.shareButtonText}
            </Button>
          </Box>
        </GridColumn>
      )}
      <GridColumn>
        <Table spacing="default">
          <TableHeaderRow>
            <TableRow>
              {[
                isMultiInvoicePayAvailable && (
                  <TableHeaderSelectionCell
                    key="select"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onClick={handleSelectAll}
                  />
                ),
                <TableHeaderCell
                  key="invoiceNo"
                  onClick={() => handleSortClick(SortableColumnKeys.InvoiceNo)}
                  sortable={true}
                  sortKey={SortableColumnKeys.InvoiceNo}
                  sortDirection={
                    sortProperty === SortableColumnKeys.InvoiceNo
                      ? sortDirection
                      : undefined
                  }
                >
                  Invoice ID
                </TableHeaderCell>,
                <TableHeaderCell
                  key="poNumber"
                  onClick={() => handleSortClick(SortableColumnKeys.PONumber)}
                  sortable={true}
                  sortKey={SortableColumnKeys.PONumber}
                  sortDirection={
                    sortProperty === SortableColumnKeys.PONumber
                      ? sortDirection
                      : undefined
                  }
                >
                  P.O. number
                </TableHeaderCell>,
                <TableHeaderCell
                  key="shipTo"
                  onClick={() => handleSortClick(SortableColumnKeys.ShipTo)}
                  sortable={true}
                  sortKey={SortableColumnKeys.ShipTo}
                  sortDirection={
                    sortProperty === SortableColumnKeys.ShipTo
                      ? sortDirection
                      : undefined
                  }
                  grow={1.5}
                >
                  Shipping location
                </TableHeaderCell>,
                <TableHeaderCell
                  key="total"
                  onClick={() => handleSortClick(SortableColumnKeys.Total)}
                  sortable={true}
                  sortKey={SortableColumnKeys.Total}
                  sortDirection={
                    sortProperty === SortableColumnKeys.Total
                      ? sortDirection
                      : undefined
                  }
                >
                  Amount
                </TableHeaderCell>,
                <TableHeaderCell
                  key="dueDate"
                  onClick={() => handleSortClick(SortableColumnKeys.DueDate)}
                  sortable={true}
                  sortKey={SortableColumnKeys.DueDate}
                  sortDirection={
                    sortProperty === SortableColumnKeys.DueDate
                      ? sortDirection
                      : undefined
                  }
                >
                  Due date
                </TableHeaderCell>,
                <TableHeaderCell
                  key="status"
                  onClick={() => handleSortClick(SortableColumnKeys.Status)}
                  sortable={true}
                  sortKey={SortableColumnKeys.Status}
                  sortDirection={
                    sortProperty === SortableColumnKeys.Status
                      ? sortDirection
                      : undefined
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "spacing-2"
                    }}
                  >
                    Status
                    <Tooltip content={statusLegend}>
                      <Icon
                        sx={{ pt: "spacing-2" }}
                        color="text-link"
                        icon="info"
                        size="xxs"
                      />
                    </Tooltip>
                  </Box>
                </TableHeaderCell>,
                <TableHeaderCell key="actions">Actions</TableHeaderCell>
              ].filter((cell): cell is React.ReactElement => Boolean(cell))}
            </TableRow>
          </TableHeaderRow>
          <TableBody>
            {invoiceList.map((invoice, index) => {
              const {
                invoiceNo,
                poNumber,
                shipTo,
                total,
                currency,
                dueDate,
                status
              } = invoice;
              const paymentStatus = formatInvoiceStatus(status);
              return (
                <TableRow
                  key={index}
                  selected={
                    isMultiInvoicePayAvailable &&
                    selectedInvoiceNos.includes(invoiceNo)
                  }
                >
                  {[
                    isMultiInvoicePayAvailable && (
                      <TableSelectionCell
                        key="select"
                        checked={selectedInvoiceNos.includes(invoiceNo)}
                        onClick={() => handleSelectRow(invoiceNo)}
                      />
                    ),
                    <TableCell key="invoiceNo">
                      <Link
                        href={`/your-account/invoices-and-payments/invoices/${invoiceNo}`}
                      >
                        {invoiceNo}
                      </Link>
                    </TableCell>,
                    <TableCell key="poNumber">{poNumber || "--"}</TableCell>,
                    <TableCell key="shipTo">{formatAddress(shipTo)}</TableCell>,
                    <TableCell key="total">
                      {formatCurrency(total, currency)}
                    </TableCell>,
                    <TableCell key="dueDate">
                      {dueDate ? formatTimestampDate(dueDate) : "--"}
                    </TableCell>,
                    <TableCell key="status">
                      {getPaymentStatus(paymentStatus)}
                    </TableCell>,
                    <TableCell key="actions">
                      {getActions(paymentStatus, invoiceNo)}
                    </TableCell>
                  ].filter((cell): cell is React.ReactElement => Boolean(cell))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Grid sx={{ mt: "spacing-9" }}>
          <GridColumn push={[0, 3, 5]} size={2}>
            {totalPage > 1 && (
              <Pagination
                page={currentPage}
                count={totalPage}
                onPageChange={onPageChange}
              />
            )}
          </GridColumn>
          <GridColumn push={[0, 1, 3]} size={2}>
            {totalInvoices > 10 && (
              <Dropdown
                label="Number of invoices"
                defaultOption={{ label: String(pageSize), value: pageSize }}
                dropdownOptions={[
                  { label: "10", value: 10 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 }
                ]}
                onSelectOption={({ value }) =>
                  onPageSizeChange(Number(value) as PageSize)
                }
              />
            )}
          </GridColumn>
        </Grid>
      </GridColumn>
    </Grid>
  );
};

export default InvoiceList;
